import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Printer } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

export default function PrintPage() {
  const { type } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [deliveryDate, setDeliveryDate] = useState(searchParams.get("date") || "");
  const [printData, setPrintData] = useState(null);
  const [loading, setLoading] = useState(false);

  // クエリパラメータで日付指定された場合は自動取得
  useEffect(() => {
    const queryDate = searchParams.get("date");
    if (queryDate && type) {
      setDeliveryDate(queryDate);
      // 自動的にデータ取得
      const autoFetch = async () => {
        setLoading(true);
        try {
          let endpoint = "";
          switch (type) {
            case "picking-list":
              endpoint = `/orders/print/picking-list?delivery_date=${queryDate}`;
              break;
            case "summary":
              endpoint = `/orders/print/summary?delivery_date=${queryDate}`;
              break;
            case "by-supplier":
              endpoint = `/orders/print/by-supplier?delivery_date=${queryDate}`;
              break;
            default:
              return;
          }
          const response = await axios.get(`${API}${endpoint}`, getAuthHeaders());
          setPrintData(response.data);
        } catch (error) {
          toast.error("データの取得に失敗しました");
        } finally {
          setLoading(false);
        }
      };
      autoFetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, type]);

  const getTitle = () => {
    switch (type) {
      case "picking-list":
        return "ピッキングリスト";
      case "summary":
        return "商品集計";
      case "by-supplier":
        return "仕入先別集計";
      default:
        return "印刷";
    }
  };

  const fetchPrintData = async () => {
    if (!deliveryDate) {
      toast.error("納品日を選択してください");
      return;
    }

    setLoading(true);
    try {
      let endpoint = "";
      switch (type) {
        case "picking-list":
          endpoint = `/orders/print/picking-list?delivery_date=${deliveryDate}`;
          break;
        case "summary":
          endpoint = `/orders/print/summary?delivery_date=${deliveryDate}`;
          break;
        case "by-supplier":
          endpoint = `/orders/print/by-supplier?delivery_date=${deliveryDate}`;
          break;
        default:
          return;
      }

      const response = await axios.get(`${API}${endpoint}`, getAuthHeaders());
      setPrintData(response.data);
    } catch (error) {
      toast.error("データの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header - No print */}
      <div className="no-print bg-white border-b border-slate-200 p-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate(-1)}
              variant="ghost"
              className="hover:bg-slate-100"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              戻る
            </Button>
            <h1 className="text-xl font-semibold font-chivo">{getTitle()}</h1>
          </div>
          <Button
            onClick={handlePrint}
            disabled={!printData}
            data-testid="print-action-button"
            className="bg-slate-900 text-white hover:bg-slate-800"
          >
            <Printer className="h-4 w-4 mr-2" />
            印刷
          </Button>
        </div>
      </div>

      {/* Controls - No print */}
      <div className="no-print max-w-7xl mx-auto p-6">
        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Label htmlFor="delivery-date" className="text-sm font-medium">
                納品日
              </Label>
              <Input
                id="delivery-date"
                type="date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
                data-testid="print-delivery-date-input"
                className="mt-1 h-9 rounded-sm"
              />
            </div>
            <Button
              onClick={fetchPrintData}
              disabled={loading}
              data-testid="fetch-print-data-button"
              className="bg-slate-900 text-white hover:bg-slate-800"
            >
              {loading ? "読み込み中..." : "データ取得"}
            </Button>
          </div>
        </div>
      </div>

      {/* Print Content */}
      {printData && (
        <div className="print-content max-w-7xl mx-auto p-6">
          <div className="bg-white border border-slate-200 rounded-sm p-8">
            {/* Print Header */}
            <div className="mb-6 border-b border-slate-200 pb-4">
              <h2 className="text-2xl font-bold font-chivo">{getTitle()}</h2>
              <p className="text-sm text-slate-600 mt-1">
                納品日: {deliveryDate}
              </p>
            </div>

            {/* Picking List */}
            {type === "picking-list" && (
              <table className="w-full border border-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="border border-slate-200 px-4 py-2 text-left text-xs font-semibold">
                      店舗名
                    </th>
                    <th className="border border-slate-200 px-4 py-2 text-left text-xs font-semibold">
                      商品コード
                    </th>
                    <th className="border border-slate-200 px-4 py-2 text-left text-xs font-semibold">
                      商品名
                    </th>
                    <th className="border border-slate-200 px-4 py-2 text-right text-xs font-semibold">
                      数量
                    </th>
                    <th className="border border-slate-200 px-4 py-2 text-center text-xs font-semibold">
                      単位
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {printData.map((item, index) => (
                    <tr key={index}>
                      <td className="border border-slate-200 px-4 py-2 text-sm">
                        {item.store_name}
                      </td>
                      <td className="border border-slate-200 px-4 py-2 text-sm font-mono">
                        {item.product_code}
                      </td>
                      <td className="border border-slate-200 px-4 py-2 text-sm">
                        {item.product_name}
                      </td>
                      <td className="border border-slate-200 px-4 py-2 text-sm font-mono text-right">
                        {item.quantity.toFixed(2)}
                      </td>
                      <td className="border border-slate-200 px-4 py-2 text-sm text-center">
                        {item.unit}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Summary */}
            {type === "summary" && (
              <table className="w-full border border-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="border border-slate-200 px-4 py-2 text-left text-xs font-semibold">
                      商品コード
                    </th>
                    <th className="border border-slate-200 px-4 py-2 text-left text-xs font-semibold">
                      商品名
                    </th>
                    <th className="border border-slate-200 px-4 py-2 text-right text-xs font-semibold">
                      合計数量
                    </th>
                    <th className="border border-slate-200 px-4 py-2 text-center text-xs font-semibold">
                      単位
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {printData.map((item, index) => (
                    <tr key={index}>
                      <td className="border border-slate-200 px-4 py-2 text-sm font-mono">
                        {item.product_code}
                      </td>
                      <td className="border border-slate-200 px-4 py-2 text-sm">
                        {item.product_name}
                      </td>
                      <td className="border border-slate-200 px-4 py-2 text-sm font-mono text-right">
                        {item.total_quantity.toFixed(2)}
                      </td>
                      <td className="border border-slate-200 px-4 py-2 text-sm text-center">
                        {item.unit}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* By Supplier */}
            {type === "by-supplier" && (
              <div className="space-y-6">
                {printData.map((supplier, index) => (
                  <div key={index}>
                    <h3 className="font-semibold text-lg mb-2">
                      {supplier.supplier_name} ({supplier.supplier_code})
                    </h3>
                    <table className="w-full border border-slate-200">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="border border-slate-200 px-4 py-2 text-left text-xs font-semibold">
                            商品コード
                          </th>
                          <th className="border border-slate-200 px-4 py-2 text-left text-xs font-semibold">
                            商品名
                          </th>
                          <th className="border border-slate-200 px-4 py-2 text-right text-xs font-semibold">
                            数量
                          </th>
                          <th className="border border-slate-200 px-4 py-2 text-center text-xs font-semibold">
                            単位
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {supplier.items.map((item, itemIndex) => (
                          <tr key={itemIndex}>
                            <td className="border border-slate-200 px-4 py-2 text-sm font-mono">
                              {item.product_code}
                            </td>
                            <td className="border border-slate-200 px-4 py-2 text-sm">
                              {item.product_name}
                            </td>
                            <td className="border border-slate-200 px-4 py-2 text-sm font-mono text-right">
                              {item.quantity.toFixed(2)}
                            </td>
                            <td className="border border-slate-200 px-4 py-2 text-sm text-center">
                              {item.unit}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {!printData && !loading && (
        <div className="max-w-7xl mx-auto p-6">
          <div className="text-center py-12 text-slate-500">
            納品日を選択してデータを取得してください
          </div>
        </div>
      )}
    </div>
  );
}